/**
 * @file Общая оболочка авторизованных экранов.
 *
 * Outlet — место текущей страницы React Router. Панель задач находится рядом
 * с ним, поэтому её отображение не зависит от выбранной папки.
 */
import { useEffect, useRef } from 'react'
import { Link, Outlet, useMatch, useNavigate } from 'react-router-dom'

import TaskList from '../components/TaskList'
import { useCustomer } from '../context/useCustomer'
import { storageMessages } from '../features/storages/messages'
import { useLocale } from '../i18n/useLocale'
import './DashboardLayout.css'

const DashboardLayout = () => {
    const { deleteCustomerSession } = useCustomer()
    const { locale, changeLocale } = useLocale()
    const text = storageMessages[locale]

    // Навигация не управляет задачами: панель передач находится вне Outlet.
    const navigate = useNavigate()
    const storageRoute = useMatch('/dashboard/box/:boxId')

    const accountMenu = useRef<HTMLDetailsElement>(null)

    useEffect(() => {
        function closeOutside(event: PointerEvent) {
            if (
                event.target instanceof Node &&
                !accountMenu.current?.contains(event.target)
            ) {
                accountMenu.current?.removeAttribute('open')
            }
        }

        function closeOnEscape(event: KeyboardEvent) {
            if (event.key === 'Escape' && accountMenu.current?.open) {
                accountMenu.current.removeAttribute('open')
                accountMenu.current.querySelector('summary')?.focus()
            }
        }

        document.addEventListener('pointerdown', closeOutside)
        document.addEventListener('keydown', closeOnEscape)

        return () => {
            document.removeEventListener('pointerdown', closeOutside)
            document.removeEventListener('keydown', closeOnEscape)
        }
    }, [])

    const handleLogout = async () => {
        await deleteCustomerSession()
        navigate('/')
    }

    return (
        <div className='dashboard'>
            <a className='dashboard-skip' href='#dashboard-content'>
                {text.skipToContent}
            </a>

            <header className='dashboard-header'>
                <div className='dashboard-header-inner'>
                    <Link className='dashboard-brand' to='/dashboard/boxes'>
                        Cloud-box
                    </Link>

                    <details className='dashboard-account' ref={accountMenu}>
                        <summary>
                            {text.account}
                            <span aria-hidden='true'>⌄</span>
                        </summary>

                        <div className='dashboard-account-panel'>
                            <label htmlFor='dashboard-language'>
                                {text.language}
                            </label>
                            <select
                                id='dashboard-language'
                                className='dashboard-language'
                                value={locale}
                                onChange={(event) =>
                                    changeLocale(
                                        event.target.value === 'ru'
                                            ? 'ru'
                                            : 'en',
                                    )
                                }
                            >
                                <option value='ru' lang='ru'>
                                    Русский
                                </option>
                                <option value='en' lang='en'>
                                    English
                                </option>
                            </select>

                            <button
                                className='logout-button'
                                onClick={handleLogout}
                            >
                                {text.logout}
                            </button>
                        </div>
                    </details>
                </div>
            </header>

            {/* Outlet заменяется при навигации. Общая шапка и Provider передач при этом остаются. */}
            <main
                className='dashboard-content'
                id='dashboard-content'
                tabIndex={-1}
            >
                {storageRoute && (
                    <nav
                        className='dashboard-breadcrumbs'
                        aria-label={text.navigation}
                    >
                        <Link to='/dashboard/boxes'>{text.title}</Link>
                        <span aria-hidden='true'>/</span>
                        <span aria-current='page'>
                            {storageRoute.params.boxId}
                        </span>
                    </nav>
                )}

                <Outlet />
            </main>

            {/* Панель находится вне текущей страницы, поэтому задачи видны после перехода в другую папку. */}
            <TaskList />
        </div>
    )
}

export default DashboardLayout
